import React, { useCallback, useEffect, useState } from 'react';
import { fetchLeadSummaryHistory, SummaryHistoryEntry } from '../../services/api';

interface Props { leadId: number }

/**
 * Collapsible "Summary History" section on the Lead Detail Timeline tab.
 * Shows every AI-generated summary snapshot with timestamp, sentiment,
 * and what triggered it (SIM call, WhatsApp message, scheduler refresh).
 *
 * Data source: GET /api/leads/{id}/summary-history (append-only
 * lead_summary_snapshots table, populated by AnalyzerDecisionApplier
 * and InteractionSummaryService).
 *
 * No backfill: history starts accumulating from 2026-08-27 onward.
 * Older leads may show empty history for a while.
 */
export default function SummaryHistory({ leadId }: Props) {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState<SummaryHistoryEntry[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchLeadSummaryHistory(leadId, 20);
      setEntries(res.data);
      setLoaded(true);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  // Lazy load — only fetch when the user actually expands the section.
  useEffect(() => {
    if (open && !loaded && !loading) load();
  }, [open, loaded, loading, load]);

  return (
    <div style={{ background: '#f8fafc', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 14px', marginBottom: 16 }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                 background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                 fontSize: 12, fontWeight: 700, color: '#475569', letterSpacing: 0.4 }}
      >
        <span>{open ? '▼' : '▶'}</span>
        <span>SUMMARY HISTORY {entries.length > 0 ? `(${entries.length})` : ''}</span>
      </button>

      {open && loading && (
        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 8 }}>Loading…</div>
      )}
      {open && error && (
        <div style={{ fontSize: 12, color: '#dc2626', marginTop: 8 }}>Failed to load: {error}</div>
      )}
      {open && loaded && entries.length === 0 && (
        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 8 }}>
          No summary history yet. New summaries will appear here as the analyzer runs.
        </div>
      )}
      {open && loaded && entries.length > 0 && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {entries.map(e => <SnapshotCard key={e.id} snap={e} />)}
        </div>
      )}
    </div>
  );
}

function SnapshotCard({ snap }: { snap: SummaryHistoryEntry }) {
  const when = new Date(snap.generated_at);
  const relative = relativeTime(when);

  return (
    <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
        <span style={{ fontSize: 11, color: '#64748b' }} title={when.toLocaleString()}>{relative}</span>
        <TriggerBadge trigger={snap.trigger_type} triggerRef={snap.trigger_ref} />
        <PipelineBadge pipeline={snap.source_pipeline} />
        {snap.sentiment && <SentimentBadge sentiment={snap.sentiment} />}
      </div>
      <div style={{ fontSize: 13, color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{snap.summary}</div>
    </div>
  );
}

function TriggerBadge({ trigger, triggerRef }: { trigger: string; triggerRef: Record<string, unknown> | null }) {
  const labels: Record<string, string> = {
    sim_call:          '📞 SIM call',
    whatsapp:          '💬 WhatsApp',
    scheduler_refresh: '🕐 Scheduler',
    note:              '📝 Note',
    portal:            '🌐 Portal',
    manual:            '👤 Manual',
    ai_voice:          '🎤 AI call',
  };
  const label = labels[trigger] ?? trigger;
  // If sim_call, tack duration on the badge.
  const suffix = trigger === 'sim_call' && triggerRef && typeof triggerRef.duration_seconds === 'number'
    ? ` · ${formatDuration(triggerRef.duration_seconds as number)}`
    : '';
  return (
    <span style={{ fontSize: 11, fontWeight: 500, padding: '1px 8px', borderRadius: 20, background: '#eef2ff', color: '#4338ca' }}>
      {label}{suffix}
    </span>
  );
}

function PipelineBadge({ pipeline }: { pipeline: string }) {
  const isLegacy = pipeline === 'legacy';
  return (
    <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 20,
                   background: isLegacy ? '#f1f5f9' : '#f0fdf4',
                   color:      isLegacy ? '#64748b' : '#166534' }}>
      {pipeline}
    </span>
  );
}

function SentimentBadge({ sentiment }: { sentiment: string }) {
  const colors: Record<string, { bg: string; fg: string }> = {
    positive: { bg: '#dcfce7', fg: '#166534' },
    negative: { bg: '#fee2e2', fg: '#991b1b' },
    neutral:  { bg: '#f1f5f9', fg: '#475569' },
  };
  const c = colors[sentiment] ?? colors.neutral;
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 20, background: c.bg, color: c.fg }}>
      {sentiment}
    </span>
  );
}

function relativeTime(d: Date): string {
  const ms = Date.now() - d.getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60)  return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s === 0 ? `${m}m` : `${m}m ${s}s`;
}
